"use client";

import React from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, CircleCheck as CheckCircle2, Clock, Send, Loader } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const contactHighlights = [{
  id: 1,
  title: "Direct Consultant Access",
  desc: "You work directly with Yassine Essaadi, not account managers."
}, {
  id: 2,
  title: "Tailored Strategy",
  desc: "Bespoke SEO solutions focused on verified business metrics."
}, {
  id: 3,
  title: "Transparent Reporting",
  desc: "Clear deliverables, realistic timelines, and honest communication."
}];
const topicOptions = [{
  value: "free-seo-audit",
  label: "Free SEO Audit Request"
}, {
  value: "national-seo",
  label: "National SEO Strategy"
}, {
  value: "international-seo",
  label: "International SEO"
}, {
  value: "local-seo",
  label: "Local SEO"
}, {
  value: "b2b-seo",
  label: "B2B SEO & Lead Generation"
}, {
  value: "ecommerce-seo",
  label: "Ecommerce SEO"
}, {
  value: "technical-seo",
  label: "Technical SEO Audit"
}, {
  value: "ai-search",
  label: "AI Search Optimisation"
}, {
  value: "general",
  label: "General SEO Inquiry"
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-lg border border-border bg-card p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="font-serif text-2xl font-bold text-foreground">
          Message Received
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {successMessage}
        </p>
        <Button type="button" onClick={resetForm} variant="outline" className="mt-6">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="name" rules={{
      required: "Please enter your name",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Your name</FormLabel>
            <FormControl>
              <Input placeholder="John Doe" className="bg-background focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Please enter your email address",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@company.com" className="bg-background focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="reason" rules={{
      required: "Please select a topic"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Topic</FormLabel>
            <FormControl>
              <select {...field} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-base text-foreground shadow-xs transition-colors outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-primary md:text-sm">
                <option value="" disabled>
                  Select a topic...
                </option>
                {topicOptions.map((opt, i) => <option key={opt.value} value={opt.value} data-index={i}>
                    {opt.label}
                  </option>)}
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please detail your SEO goals or question",
      minLength: {
        value: 10,
        message: "Please provide at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Message</FormLabel>
            <FormControl>
              <Textarea rows={4} placeholder="Tell me about your SEO goals..." className="bg-background focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-primary text-primary-foreground transition-all duration-200 hover:bg-primary/90 hover:shadow-lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Sending Inquiry...
          </> : <>
            <Send className="mr-2 size-4" />
            Send Inquiry
          </>}
      </Button>
    </Form>;
}
export default function InquiryForm() {
  const [searchParams] = useSearchParams();
  const topicParam = searchParams.get("topic");
  const initialReason = topicParam === "free-seo-audit" ? "free-seo-audit" : "";
  return <section id="inquiryform" data-nav="light" className="bg-card text-card-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8 items-start">
          
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-5 flex flex-col justify-between">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                INQUIRY FORM
              </span>
              <h2 className="mt-3 font-serif text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
                Let&apos;s Discuss Your SEO Growth
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                I personally review every message within 24 hours. When you reach out, you work directly with me—no account managers, junior staff, or sales reps.
              </p>

              <div className="mt-6 flex items-center gap-2 text-sm text-foreground/80 font-medium">
                <Clock className="size-4 text-primary" />
                <span>Typical response time: Under 24 hours</span>
              </div>

              <div className="mt-8 space-y-4">
                {contactHighlights.map((item, i) => <div key={item.id} data-index={i} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-primary" />
                    <div>
                      <h3 className="font-serif text-sm font-semibold text-foreground">
                        {item.title}
                      </h3>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="mt-10 rounded-xl border border-border bg-background p-6 shadow-sm">
              <h3 className="font-serif text-sm font-bold uppercase tracking-wider text-foreground">
                Direct Details
              </h3>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <Mail className="size-4 text-primary shrink-0" />
                  <a href="mailto:contact@ye-consultancy.com" className="text-foreground transition-colors hover:text-primary">
                    contact@ye-consultancy.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="size-4 text-primary shrink-0" />
                  <a href="tel:+447490984430" className="text-foreground transition-colors hover:text-primary">
                    +44 7490984430
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="size-4 text-primary shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">
                    Windmill Hill Business Park, Whitehill Way, Swindon SN5 6QR
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="lg:col-span-7">
            <Card className="border-border bg-background shadow-md">
              <CardContent className="p-6 md:p-8 lg:p-10">
                <div className="mb-6">
                  <h3 className="font-serif text-xl font-bold text-foreground md:text-2xl">
                    Start a Conversation
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Fill out the form below to discuss your project, technical audit, or SEO strategy.
                  </p>
                </div>

                <FormProvider formId="contact_form_contact" sectionName="InquiryForm" defaultValues={{
                name: "",
                email: "",
                reason: initialReason,
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                reason: {
                  required: true
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Send Inquiry" successMessage="Thank you! Your message has been sent. I will personally review it and get back to you within 24 hours." errorMessage="An error occurred while submitting your message. Please try again or email me directly.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}